import { type FC, useMemo } from "react";
import { ArrowRight, Home } from "lucide-react";

import { useAppContext } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { Button } from "@/shared/components/ui/button";
import { Badge } from "@/shared/components/ui/badge";
import { OptionCard } from "@/shared/components/ui/OptionCard";
import { RadioGroup } from "@/shared/components/ui/radio-group";
import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

type InsuranceOption = "yes" | "no" | "not_sure";

interface PropertyInsuranceQuestionProps extends FlowCustomComponentProps {
  propertyId: number;
}

export const PropertyInsuranceQuestion: FC<PropertyInsuranceQuestionProps> = ({
  propertyId,
  answers,
  setAnswer,
  onComplete,
}) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const { assets } = useEstateItems(estateId);

  const property = useMemo(() => assets.find((p) => p.id === propertyId), [assets, propertyId]);

  const answerId = `has_insurance_${propertyId}`;
  const rawAnswer = answers[answerId];
  const selected: InsuranceOption | null =
    rawAnswer === "yes" || rawAnswer === "no" || rawAnswer === "not_sure" ? rawAnswer : null;

  const selectOption = (option: InsuranceOption) => {
    setAnswer(answerId, option);
  };

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col">
      {property && (
        <div className="mb-4 flex justify-center">
          <Badge variant="outline" className="gap-1.5 border-none bg-white py-1.5 text-secondary">
            <Home className="h-3.5 w-3.5 text-purple-600" aria-hidden="true" />
            {property.item_name}
          </Badge>
        </div>
      )}

      <h2 className="text-center">Is this property insured?</h2>
      <p className="mt-2 text-center">
        Most homes have a policy. Keeping it active protects the property until it's transferred or
        sold.
      </p>

      <RadioGroup
        value={selected ?? undefined}
        onValueChange={(v) => selectOption(v as InsuranceOption)}
        className="mt-8 flex flex-col gap-3"
      >
        <OptionCard
          label="Yes, there's a policy"
          selected={selected === "yes"}
          onClick={() => selectOption("yes")}
          variant="radio"
          value="yes"
        />
        <OptionCard
          label="No policy"
          selected={selected === "no"}
          onClick={() => selectOption("no")}
          variant="radio"
          value="no"
        />
        <OptionCard
          label="Not sure"
          selected={selected === "not_sure"}
          onClick={() => selectOption("not_sure")}
          variant="radio"
          value="not_sure"
        />
      </RadioGroup>

      <div className="mt-10 flex flex-col items-center">
        <Button
          variant="brand"
          rounded={true}
          onClick={() => onComplete()}
          disabled={selected === null}
          className="w-full"
        >
          Continue <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
